import { CommonModule } from '@angular/common';
import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { TagComponent } from '../../../../../shared/components/tag/tag.component';
import { AccordionModule } from 'primeng/accordion';
import { BadgeComponent } from '../../../../../shared/components/badge/badge.component';
import { cargoActionType, rideStopCargoStatus } from '../../../../../shared/enums/rideStopCargoStatus';
import { StopPlaceType } from '../../../../../shared/enums/common';
import { RideStatusType } from '../../../../../shared/enums/rideStatusType';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageService } from 'primeng/api';
import { DeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import { RequestBuilderService } from '../../../../../shared/services/request-builder.service';
import { DialogModule } from 'primeng/dialog';
import moment from 'moment';

@Component({
  selector: 'app-complete-ride-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonComponent, TagComponent, AccordionModule, BadgeComponent, SelectModule, DatePickerModule, DialogModule],
  providers: [DialogService],
  templateUrl: './complete-ride-dialog.component.html',
  styleUrl: './complete-ride-dialog.component.scss',
})
export class CompleteRideDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly fb = inject(FormBuilder);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly requestBuilder = inject(RequestBuilderService);

  completeRideForm!: FormGroup;

  rideTimeline = signal(this.config.data.ride.rideTimeline ?? []);
  accordionActiveIndex: number[] = [];
  cargoActionType = signal(cargoActionType);
  stopPlaceType = signal(StopPlaceType);
  confirmationDialogVisible = false;
  passengerStatusOptions = signal<Map<string, { name: string; id: number }[]>>(new Map());
  RideStatusType = RideStatusType;

  // Computed signal to determine which stops should show arrival time input
  // This is calculated once and cached until rideTimeline changes
  stopsToShowArrivalTime = computed(() => {
    const timeline = this.rideTimeline();
    const stopsSet = new Set<number>();

    timeline.forEach((stop: any, index: number) => {
      if (this.shouldShowArrivalTimeInput(stop)) {
        stopsSet.add(index);
      }
    });

    return stopsSet;
  });

  ngOnInit() {
    console.log(this.config.data);
    this.initializeForm();
    this.setOptionsForAllPassengers();
    this.setupStatusChangeListeners();
    this.setupArrivalTimeValidation();

    // Open all accordion tabs by default
    this.accordionActiveIndex = this.rideTimeline().map((_: any, index: number) => index);
  }

  toggleActiveAccordionTab(index: number) {
    const currentIndex = this.accordionActiveIndex.indexOf(index);

    if (currentIndex === -1) {
      this.accordionActiveIndex = [...this.accordionActiveIndex, index];
    } else {
      this.accordionActiveIndex = [...this.accordionActiveIndex.slice(0, currentIndex), ...this.accordionActiveIndex.slice(currentIndex + 1)];
    }
  }

  accordionActiveIndexChange(index: string | number | string[] | number[] | null | undefined) {
    if (Array.isArray(index)) {
      this.accordionActiveIndex = index as number[];
    } else if (index == null) {
      this.accordionActiveIndex = [];
    } else {
      this.accordionActiveIndex = [index] as number[];
    }
  }

  initializeForm() {
    this.completeRideForm = this.fb.group({
      stops: this.fb.array([]),
    });

    // Build form array for each stop
    const timeline = this.rideTimeline();
    timeline.forEach((stop: any, stopIndex: number) => {
      this.stops.push(this.createStopFormGroup(stop, stopIndex));
    });
  }

  get stops(): FormArray {
    return this.completeRideForm.get('stops') as FormArray;
  }

  getStopFormGroup(index: number): FormGroup {
    return this.stops.at(index) as FormGroup;
  }

  getCargoFormGroup(stopIndex: number, cargoIndex: number): FormGroup {
    return (this.stops.at(stopIndex).get('cargoes') as FormArray).at(cargoIndex) as FormGroup;
  }

  createStopFormGroup(stop: any, stopIndex: number): FormGroup {
    const stopGroup = this.fb.group({
      stopId: [stop.stopId ?? 0],
      actualArrivalTime: [stop.actualArrivalTime ?? '', Validators.required],
      cargoes: this.fb.array([]),
    });

    // Add cargoes (passengers) for this stop
    const cargoesArray = stopGroup.get('cargoes') as FormArray;
    stop.cargoes?.forEach((cargo: any, cargoIndex: number) => {
      cargoesArray.push(this.createCargoFormGroup(cargo, stopIndex, cargoIndex));
    });

    // Check if arrival time input should be shown based on original data
    if (!this.stopsToShowArrivalTime().has(stopIndex)) {
      const arrivalTimeControl = stopGroup.get('actualArrivalTime');
      if (arrivalTimeControl) {
        arrivalTimeControl.clearValidators();
        arrivalTimeControl.updateValueAndValidity();
        arrivalTimeControl.disable();
      }
    }

    return stopGroup;
  }

  createCargoFormGroup(cargo: any, stopIndex: number, cargoIndex: number): FormGroup {
    // If passenger already has a status, don't require selection (won't show dropdown)
    const hasExistingStatus = cargo.statusId && cargo.statusId !== RideStatusType.None;

    return this.fb.group({
      cargoId: [cargo.stopCargoId ?? 0],
      rideStatusId: [hasExistingStatus ? cargo.statusId : null, hasExistingStatus ? [] : Validators.required],
    });
  }

  setOptionsForAllPassengers() {
    const timeline = this.rideTimeline();

    timeline.forEach((stop: any, stopIndex: number) => {
      stop.cargoes?.forEach((passenger: any, cargoIndex: number) => {
        const key = `${stopIndex}-${cargoIndex}`;
        const options = this.getOptionsForPassenger(passenger);

        const currentMap = this.passengerStatusOptions();
        currentMap.set(key, options);
        this.passengerStatusOptions.set(new Map(currentMap));
      });
    });
  }

  getOptionsForPassenger(passenger: any): { name: string; id: number }[] {
    const options: { name: string; id: number }[] = [];
    const isPickUp = passenger?.actionType?.toLowerCase() === 'pick-up';

    if (isPickUp && passenger?.statusId === RideStatusType.None) {
      options.push({ name: 'Pick-up', id: rideStopCargoStatus.PickedUp }, { name: 'No-show', id: RideStatusType.NoShow });
    } else if (isPickUp && passenger?.statusId === rideStopCargoStatus.PickedUp) {
      options.push({ name: 'No-show', id: RideStatusType.NoShow });
    } else if (isPickUp && passenger?.statusId === RideStatusType.NoShow) {
      options.push({ name: 'Pick-up', id: rideStopCargoStatus.PickedUp });
    } else if (!isPickUp && passenger?.statusId === RideStatusType.None) {
      options.push({ name: 'Dropped-off', id: rideStopCargoStatus.DroppedOff });
    }

    return options;
  }

  getPassengerOptions(stopIndex: number, cargoIndex: number): { name: string; id: number }[] {
    const key = `${stopIndex}-${cargoIndex}`;
    return this.passengerStatusOptions().get(key) || [];
  }

  setupStatusChangeListeners() {
    const timeline = this.rideTimeline();

    // Only set up listeners for PICKUP stops to avoid infinite loops
    timeline.forEach((stop: any, stopIndex: number) => {
      stop.cargoes?.forEach((passenger: any, cargoIndex: number) => {
        // Only listen to pickup stops (where no-show can be selected)
        const isPickup = passenger?.actionType?.toLowerCase() === 'pick-up';

        if (isPickup) {
          const cargoFormGroup = this.getCargoFormGroup(stopIndex, cargoIndex);
          const statusControl = cargoFormGroup.get('rideStatusId');

          if (statusControl) {
            statusControl.valueChanges.subscribe((newStatus: number) => {
              this.handlePickupStatusChange(stopIndex, cargoIndex, newStatus, passenger);
            });
          }
        }
      });
    });
  }

  setupArrivalTimeValidation() {
    // Set up value change listeners for each stop's arrival time
    this.stops.controls.forEach((stopControl, stopIndex) => {
      const arrivalTimeControl = stopControl.get('actualArrivalTime');

      if (arrivalTimeControl) {
        arrivalTimeControl.valueChanges.subscribe(value => {
          // Only validate when there's an actual value (Date object)
          if (value instanceof Date) {
            // When a stop's time changes, validate this stop and all subsequent stops
            this.validateSingleStopArrivalTime(stopIndex);
            this.validateArrivalTimesFromIndex(stopIndex + 1);
          }
        });
      }
    });
  }

  private validateArrivalTimesFromIndex(startIndex: number) {
    // Validate all stops starting from the given index
    for (let i = startIndex; i < this.stops.length; i++) {
      this.validateSingleStopArrivalTime(i);
    }
  }

  private validateSingleStopArrivalTime(stopIndex: number) {
    const currentStop = this.getStopFormGroup(stopIndex);
    const currentTimeControl = currentStop.get('actualArrivalTime');

    if (!currentTimeControl?.value || !(currentTimeControl.value instanceof Date)) return;

    const selectedTime = currentTimeControl.value;
    const stop = this.rideTimeline()[stopIndex];

    if (this.isBeforeRideStartTime(selectedTime, currentTimeControl)) return;
    if (this.isBeforePreviousStopTime(stopIndex, selectedTime, currentTimeControl)) return;
    if (this.isTooLateAfterScheduled(stop, selectedTime, currentTimeControl)) return;

    // If all validations pass, clear errors
    this.clearInvalidTimeError(currentTimeControl);
  }

  private isBeforeRideStartTime(selectedTime: Date, control: any): boolean {
    // Validation 1: Check if arrival time is before ride start time (first stop's scheduled time)
    // This applies to ALL stops, not just the first one
    const firstStop = this.rideTimeline()[0];
    const firstStopScheduledTime = firstStop.scheduledArrivalTime;
    if (firstStopScheduledTime) {
      const rideStartTime = this.parseTimeStringToDate(firstStopScheduledTime);
      if (rideStartTime && moment(selectedTime).isBefore(moment(rideStartTime))) {
        control.setErrors({ beforeRideStart: true });
        return true;
      }
    }
    return false;
  }

  private isBeforePreviousStopTime(stopIndex: number, selectedTime: Date, control: any): boolean {
    // Validation 2: For non-first stops, check against previous stop's arrival time
    if (stopIndex > 0) {
      const previousStop = this.getStopFormGroup(stopIndex - 1);
      const previousTime = previousStop.get('actualArrivalTime')?.value;

      if (previousTime && previousTime instanceof Date) {
        if (moment(selectedTime).isSameOrBefore(moment(previousTime))) {
          control.setErrors({ invalidTime: true });
          return true;
        }
      }
    }
    return false;
  }

  private isTooLateAfterScheduled(stop: any, selectedTime: Date, control: any): boolean {
    // Validation 3: Check if arrival time is more than 4 hours after scheduled arrival time
    const scheduledArrivalTime = stop.scheduledArrivalTime;
    if (scheduledArrivalTime) {
      const scheduledTime = this.parseTimeStringToDate(scheduledArrivalTime);
      if (scheduledTime) {
        // Calculate 4 hours after scheduled time
        const maxAllowedTime = moment(scheduledTime).add(4, 'hours');

        if (moment(selectedTime).isAfter(maxAllowedTime)) {
          control.setErrors({ tooLate: true });
          return true;
        }
      }
    }
    return false;
  }

  private parseTimeStringToDate(timeString: string): Date | null {
    if (!timeString) return null;

    // Try parsing with different time formats that moment supports
    const formats = ['h:mm A', 'h:mm:ss A', 'H:mm', 'H:mm:ss', 'hh:mm A', 'hh:mm:ss A'];
    const parsed = moment(timeString, formats, true);
    console.log(parsed);
    console.log(parsed.toDate());

    if (!parsed.isValid()) return null;

    return parsed.toDate();
  }

  private clearInvalidTimeError(control: any) {
    const errors = control.errors;
    if (!errors) return;

    delete errors['invalidTime'];
    delete errors['beforeRideStart'];
    delete errors['tooLate'];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  handlePickupStatusChange(pickupStopIndex: number, pickupCargoIndex: number, newStatus: number, pickupPassenger: any) {
    const isNoShow = newStatus === RideStatusType.NoShow;
    const passengerCargoId = pickupPassenger.rideCargoId;
    const dropoffInfo = this.findDropoffForPassenger(passengerCargoId, pickupStopIndex);

    if (!dropoffInfo) return;

    if (isNoShow) {
      this.handleNoShowSelection(dropoffInfo);
    } else if (dropoffInfo.statusControl.disabled) {
      this.handleNoShowDeselection(dropoffInfo);
    }
  }

  private updateArrivalTimeInputState(stopIndex: number) {
    const stopFormGroup = this.getStopFormGroup(stopIndex);
    const cargoesArray = stopFormGroup.get('cargoes') as FormArray;
    const arrivalTimeControl = stopFormGroup.get('actualArrivalTime');

    if (!arrivalTimeControl || !cargoesArray) return;

    const stop = this.rideTimeline()[stopIndex];
    const hasPickup = stop.cargoes?.some((cargo: any) => {
      const isPickup = cargo.actionType?.toLowerCase() === 'pick-up';
      return isPickup;
    });

    // Always enable for pickup stops
    if (hasPickup) {
      arrivalTimeControl.setValidators([Validators.required]);
      arrivalTimeControl.updateValueAndValidity({ emitEvent: false });
      arrivalTimeControl.enable({ emitEvent: false });
      return;
    }

    // For dropoff-only stops, check if all cargoes have cancelled or no-show status
    const allCargoesCancelledOrNoShow = cargoesArray.controls.every(cargoControl => {
      const statusId = cargoControl.get('rideStatusId')?.value;
      console.log(statusId);

      return statusId === RideStatusType.NoShow || statusId === RideStatusType.CanceledInAdvance || statusId === RideStatusType.CanceledLate || statusId === RideStatusType.CanceledDueToHoliday;
    });

    if (allCargoesCancelledOrNoShow) {
      arrivalTimeControl.reset();
      arrivalTimeControl.clearValidators();
      arrivalTimeControl.updateValueAndValidity({ emitEvent: false });
      arrivalTimeControl.disable({ emitEvent: false });
    } else {
      arrivalTimeControl.setValidators([Validators.required]);
      arrivalTimeControl.updateValueAndValidity({ emitEvent: false });
      arrivalTimeControl.enable({ emitEvent: false });
    }
  }

  shouldShowArrivalTimeInput(stop: any): boolean {
    // If stop already has an actual arrival time, don't show the input
    if (stop.actualArrivalTime) {
      return false;
    }

    // Check if all cargoes already had cancelled or no-show status from original data
    const allCargoesCancelledOrNoShow = stop.cargoes?.every((cargo: any) => {
      return cargo.statusId === RideStatusType.NoShow || cargo.statusId === RideStatusType.CanceledInAdvance || cargo.statusId === RideStatusType.CanceledLate || cargo.statusId === RideStatusType.CanceledDueToHoliday;
    });

    // If all cargoes already had cancelled/no-show status, don't show input
    if (allCargoesCancelledOrNoShow) {
      return false;
    }

    // Otherwise, show the arrival time input
    return true;
  }

  private findDropoffForPassenger(passengerCargoId: number, pickupStopIndex: number) {
    const timeline = this.rideTimeline();

    for (let dropoffStopIndex = 0; dropoffStopIndex < timeline.length; dropoffStopIndex++) {
      if (dropoffStopIndex === pickupStopIndex) continue;

      const dropoffStop = timeline[dropoffStopIndex];

      for (let dropoffCargoIndex = 0; dropoffCargoIndex < (dropoffStop.cargoes?.length ?? 0); dropoffCargoIndex++) {
        const dropoffPassenger = dropoffStop.cargoes[dropoffCargoIndex];
        const isDropoff = dropoffPassenger?.actionType?.toLowerCase() === 'drop-off';

        if (dropoffPassenger.rideCargoId === passengerCargoId && isDropoff) {
          const dropoffKey = `${dropoffStopIndex}-${dropoffCargoIndex}`;
          const dropoffCargoFormGroup = this.getCargoFormGroup(dropoffStopIndex, dropoffCargoIndex);
          const statusControl = dropoffCargoFormGroup.get('rideStatusId');

          if (statusControl) {
            return { dropoffStopIndex, dropoffCargoIndex, dropoffKey, statusControl };
          }
        }
      }
    }
    return null;
  }

  private handleNoShowSelection(dropoffInfo: { dropoffStopIndex: number; dropoffCargoIndex: number; dropoffKey: string; statusControl: any }) {
    this.addNoShowOptionIfMissing(dropoffInfo.dropoffStopIndex, dropoffInfo.dropoffCargoIndex, dropoffInfo.dropoffKey);
    dropoffInfo.statusControl.setValue(RideStatusType.NoShow, { emitEvent: false });
    dropoffInfo.statusControl.disable({ onlySelf: true, emitEvent: false });

    // Update arrival time input state for dropoff stop
    this.updateArrivalTimeInputState(dropoffInfo.dropoffStopIndex);
  }

  private addNoShowOptionIfMissing(dropoffStopIndex: number, dropoffCargoIndex: number, dropoffKey: string) {
    const currentOptions = this.getPassengerOptions(dropoffStopIndex, dropoffCargoIndex);
    const hasNoShowOption = currentOptions.some(opt => opt.id === RideStatusType.NoShow);

    if (!hasNoShowOption) {
      const updatedOptions = [...currentOptions, { name: 'No-show', id: RideStatusType.NoShow }];
      const optionsMap = this.passengerStatusOptions();
      optionsMap.set(dropoffKey, updatedOptions);
      this.passengerStatusOptions.set(new Map(optionsMap));
    }
  }

  private handleNoShowDeselection(dropoffInfo: { dropoffStopIndex: number; dropoffCargoIndex: number; dropoffKey: string; statusControl: any }) {
    dropoffInfo.statusControl.enable({ emitEvent: false });

    if (dropoffInfo.statusControl.value === RideStatusType.NoShow) {
      dropoffInfo.statusControl.setValue(null, { emitEvent: false });
    }

    this.removeNoShowOption(dropoffInfo.dropoffStopIndex, dropoffInfo.dropoffCargoIndex, dropoffInfo.dropoffKey);

    // Update arrival time input state for dropoff stop
    this.updateArrivalTimeInputState(dropoffInfo.dropoffStopIndex);
  }

  private removeNoShowOption(dropoffStopIndex: number, dropoffCargoIndex: number, dropoffKey: string) {
    const currentOptions = this.getPassengerOptions(dropoffStopIndex, dropoffCargoIndex);
    const filteredOptions = currentOptions.filter(opt => opt.id !== RideStatusType.NoShow);
    const optionsMap = this.passengerStatusOptions();
    optionsMap.set(dropoffKey, filteredOptions);
    this.passengerStatusOptions.set(new Map(optionsMap));
  }

  private formatTimeForAPI(date: Date): string {
    // The API expects the time in the ride's local timezone
    const timeMoment = moment(date);

    // Use the ride's date of service instead of today's date
    const strDateOfService = this.config.data.ride.rideInfo?.strDateOfService || '';
    let dateMoment: moment.Moment;

    if (strDateOfService) {
      // Parse strDateOfService which is in MM/DD/YYYY format and set time from date parameter
      dateMoment = moment(strDateOfService, 'MM/DD/YYYY').set({
        hour: timeMoment.hour(),
        minute: timeMoment.minute(),
        second: timeMoment.second(),
        millisecond: timeMoment.millisecond(),
      });
    } else {
      // Fallback to today's date if strDateOfService is not available
      dateMoment = timeMoment;
    }

    // Format as ISO string in ride's local timezone (with Z suffix)
    return dateMoment.format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
  }

  showConfirmationDialog() {
    this.confirmationDialogVisible = true;
  }

  completeRide() {
    // Mark all fields as touched to show validation errors
    this.completeRideForm.markAllAsTouched();

    if (this.completeRideForm.valid) {
      // Use getRawValue() to include disabled controls in the form value
      const formValue = this.completeRideForm.getRawValue();

      // Format the data according to the API structure
      const payload = {
        stops: formValue.stops.map((stop: any) => {
          const stopPayload: any = {
            stopId: stop.stopId,
            cargoes: stop.cargoes.map((cargo: any) => ({
              cargoId: cargo.cargoId,
              rideStatusId: cargo.rideStatusId,
            })),
          };

          // Only include actualArrivalTime if it's a valid Date object
          // If the value is null or string (already previously saved) we will not include it
          if (stop.actualArrivalTime && stop.actualArrivalTime instanceof Date) {
            stopPayload.actualArrivalTime = this.formatTimeForAPI(stop.actualArrivalTime);
          }

          return stopPayload;
        }),
      };
      console.log(payload);

      this.requestBuilder.post(`rides/${this.config.data.uuid}/admin-manual-complete`, payload).subscribe({
        next: res => {
          this.ref.close({ confirmed: true });
        },
        error: err => {
          console.error('Error completing ride:', err);
        },
      });
    } else {
      console.log('Form is invalid', this.completeRideForm.errors);
    }
  }

  deleteRide() {
    const ride = this.config.data.ride;

    if (!ride?.uuid) {
      console.error('Ride UUID not found');
      return;
    }

    const deleteDialogRef = this.dialogService.open(DeleteDialogComponent, {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
      data: {
        action: 'deleteRide',
        header: 'Delete Ride',
        id: ride?.id ?? ride?.original_ride_id ?? '',
        uuid: ride.uuid,
        warningMessages: [`This action will notify the driver and any monitors via push Notification.`],
        inputPlaceholder: 'The reason for why you are deleting this ride',
      },
    })!;

    deleteDialogRef.onClose.subscribe((result: any) => {
      if (result?.confirmed) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Ride deleted successfully',
          life: 6000,
        });

        // Close the complete ride dialog only when deletion is confirmed
        this.ref.close({ deleted: true });
      }
    });
  }
}
